import React, { useRef, useState, useEffect } from 'react'
import cx from 'classnames'

import { SegmentedControlProps } from './interfaces'
import SegmentedControlContext, { SegmentedControlContextProps } from './SegmentedControlContext'
import { SegmentedControlItem } from './SegmentedControlItem'

import cm from './SegmentedControl.module.css'

const SegmentedControlComponent = ({
  className,
  fullWidth = false,
  onChange,
  children,
  value,
  ariaLabelledBy,
  'data-test-id': dataTestId,
}: SegmentedControlProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const itemsRef = useRef<HTMLButtonElement | null>(null)
  const contextValue: SegmentedControlContextProps = {
    value,
    onChange,
    setActiveRef: (el: HTMLButtonElement | null) => {
      itemsRef.current = el
    },
  }

  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
  })

  /** Update the indicator position and size when the active item changes */
  useEffect(() => {
    const activeElement = itemsRef.current
    if (!activeElement) return

    const updateIndicator = () => {
      setIndicatorStyle({
        left: activeElement.offsetLeft,
        width: activeElement.clientWidth,
      })
    }

    updateIndicator()

    const resizeObserver = new ResizeObserver(updateIndicator)
    resizeObserver.observe(activeElement)

    window.addEventListener('resize', updateIndicator)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateIndicator)
    }
  }, [value])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const { key } = event
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(key)) return

    const allItems = ref.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
    if (!allItems?.length) return

    const tabList = Array.from(allItems).filter((item) => !item.disabled)
    if (!tabList.length) return

    event.preventDefault()

    const currentIndex = tabList.findIndex((item) => item.value === value)
    if (currentIndex === -1) {
      return
    }

    let newIndex = currentIndex

    if (key === 'ArrowRight') {
      newIndex = (currentIndex + 1) % tabList.length
    } else if (key === 'ArrowLeft') {
      newIndex = (currentIndex - 1 + tabList.length) % tabList.length
    } else if (key === 'Home') {
      newIndex = 0
    } else if (key === 'End') {
      newIndex = tabList.length - 1
    }

    const nextItem = tabList[newIndex]
    nextItem.focus()
    onChange(nextItem.value)
  }

  return (
    <SegmentedControlContext.Provider value={contextValue}>
      <div
        ref={ref}
        role="tablist"
        aria-labelledby={ariaLabelledBy}
        onKeyDown={handleKeyDown}
        className={cx(
          cm.segmentedControl,
          {
            [cm.fullWidth]: fullWidth,
          },
          className,
        )}
        data-test-id={dataTestId}
      >
        <span
          className={cm.indicator}
          style={{
            transform: `translateX(${indicatorStyle.left}px)`,
            width: `${indicatorStyle.width}px`,
          }}
        />
        {children}
      </div>
    </SegmentedControlContext.Provider>
  )
}

export const SegmentedControl = Object.assign(SegmentedControlComponent, {
  Item: SegmentedControlItem,
})
